// Light/dark theme state (Session AFK; a third shell, the auditor portal, added Session AOR).
// The source of truth is the data-theme attribute on <html>, stamped BEFORE first paint by
// the pre-paint script index.html loads first, public/theme-init.js (inline in index.html until Session
// ASB; localStorage "ele-theme"; every shell opens LIGHT unless dark
// was deliberately chosen, Director 18 September 2026, Session AOZ), so React only ever reads and
// toggles it -- there is no provider and no flash-of-wrong-theme window. The "dark" fallbacks in
// currentTheme() and the server snapshot below are reached only when no attribute is stamped at all,
// which the pre-paint script now guarantees never happens in a browser (its storage read is guarded);
// in practice only a test environment reaches them, and the tests that rely on that keep working. The light
// values bind only inside the .office-ui / .engineer-ui / .auditor-ui shell scopes
// (src/styles/ele-theme.css), so the attribute is inert on the customer portal and public
// pages. The toggle itself is one shared preference: it does not know or care which shell it
// is rendered in.
import { useCallback, useSyncExternalStore } from "react";

export type Theme = "dark" | "light";

// Prescribed by the AFK brief. NB it deviates from the repo's `ele.<area>` localStorage
// convention (recorded in the handoff); the pre-paint script public/theme-init.js reads the same
// literal, so change both together or neither.
export const THEME_STORAGE_KEY = "ele-theme";

// Mobile-chrome tint per theme: the page ground of each palette. The dark value matches
// the static <meta name="theme-color"> in index.html; the light value is --ele-base light.
const THEME_COLOUR: Record<Theme, string> = { dark: "#050816", light: "#f8f9fa" };

// How long the ele-theme-transition class stays on <html> around a swap: the CSS runs a
// 200ms fade; 250ms covers it with a margin so the class never lingers into normal
// interaction repaints.
const TRANSITION_MS = 250;

const listeners = new Set<() => void>();

export function currentTheme(): Theme {
  if (typeof document === "undefined") return "dark";
  return document.documentElement.getAttribute("data-theme") === "light" ? "light" : "dark";
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Apply a theme: stamp <html>, persist, retint the theme-colour meta, notify hooks. */
export function setTheme(theme: Theme): void {
  const root = document.documentElement;
  if (currentTheme() !== theme) {
    root.classList.add("ele-theme-transition");
    window.setTimeout(() => root.classList.remove("ele-theme-transition"), TRANSITION_MS);
  }
  root.setAttribute("data-theme", theme);
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Private-mode/storage-denied: the theme still applies for this page's lifetime.
  }
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", THEME_COLOUR[theme]);
  listeners.forEach((l) => l());
}

/**
 * { theme, toggle, isDark } for the two shell toggles and any component that needs a
 * JS-side theme value (the engineer home skyline). Reads the live <html> attribute via
 * useSyncExternalStore, so every subscribed component re-renders on a toggle.
 */
export function useTheme(): { theme: Theme; toggle: () => void; isDark: boolean } {
  const theme = useSyncExternalStore(subscribe, currentTheme, () => "dark" as Theme);
  const toggle = useCallback(() => {
    setTheme(currentTheme() === "dark" ? "light" : "dark");
  }, []);
  return { theme, toggle, isDark: theme === "dark" };
}
